import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Plus, Loader2, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { deleteAccessService } from '@/services/deleteAccess.service';
import type { DeleteFeature, DeleteAccessPersonGrant } from '@/services/deleteAccess.service';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// ── Types ──────────────────────────────────────────────────────────────────────

type Mode = 'disabled' | 'role_wise' | 'person_wise';

// ── Constants ──────────────────────────────────────────────────────────────────

const ROLES = [
  { code: 'SUPER_ADMIN',  label: 'Super Admin' },
  { code: 'ADMIN',        label: 'Admin' },
  { code: 'MANAGER',      label: 'Manager' },
  { code: 'TEAM_LEADER',  label: 'Team Leader' },
  { code: 'USER',         label: 'User' },
];

const MODE_OPTIONS: { value: Mode; label: string; description: string }[] = [
  { value: 'disabled',    label: 'Disabled',    description: 'Any user with the relevant permission can delete records.' },
  { value: 'role_wise',   label: 'Role-wise',   description: 'Only users whose role is in the allowed list can delete records.' },
  { value: 'person_wise', label: 'Person-wise', description: 'Only specifically listed employees can delete records.' },
];

const FEATURE_LABELS: Record<DeleteFeature, string> = {
  leave:   'Leave Delete',
  penalty: 'Penalty Delete',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function getInitials(first: string | null, last: string | null): string {
  return [(first ?? '').charAt(0), (last ?? '').charAt(0)].filter(Boolean).join('').toUpperCase() || '?';
}

function fullName(first: string | null, last: string | null, code: string | null): string {
  const name = [first, last].filter(Boolean).join(' ') || '–';
  return code ? `${name} (${code})` : name;
}

// ── EmployeeCell ───────────────────────────────────────────────────────────────

function EmployeeCell({ first, last, code }: { first: string | null; last: string | null; code: string | null }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
        {getInitials(first, last)}
      </div>
      <div>
        <p className="text-xs font-medium text-foreground">
          {[first, last].filter(Boolean).join(' ') || '–'}
        </p>
        {code && <p className="text-[10px] text-muted-foreground">{code}</p>}
      </div>
    </div>
  );
}

// ── Employee Select Dropdown ───────────────────────────────────────────────────

interface EmployeeOption { value: string; label: string }

function EmployeeSelect({ value, onChange, options, placeholder }: {
  value: string;
  onChange: (id: string) => void;
  options: EmployeeOption[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2.5 text-sm hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring/20"
      >
        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-border/60 bg-popover text-popover-foreground shadow-lg">
          <div className="p-2">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-lg border border-border/60 bg-background text-foreground placeholder:text-muted-foreground px-3 py-1.5 text-sm focus:border-primary/60 focus:outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">No results</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-accent hover:text-accent-foreground ${o.value === value ? 'font-semibold text-primary' : 'text-foreground'}`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Person Modal ───────────────────────────────────────────────────────────

function AddPersonModal({ open, feature, onClose, isPending, onAdd }: {
  open: boolean;
  feature: DeleteFeature;
  onClose: () => void;
  isPending: boolean;
  onAdd: (userId: string) => void;
}) {
  const [userId, setUserId] = useState('');

  const { data: usersData } = useQuery({
    queryKey: ['users-list-for-grants'],
    queryFn: () => usersService.list({ limit: 100 }),
  });

  const options: EmployeeOption[] = (usersData?.data ?? []).map((u) => ({
    value: u.id,
    label: fullName(u.first_name, u.last_name, u.employee_code),
  }));

  const handleOpenChange = (o: boolean) => { if (!o && !isPending) onClose(); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Person — {FEATURE_LABELS[feature]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Employee who can delete</label>
          <EmployeeSelect
            value={userId}
            onChange={setUserId}
            options={options}
            placeholder="Select employee…"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button disabled={!userId || isPending} onClick={() => onAdd(userId)}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Role-wise Panel ────────────────────────────────────────────────────────────

function RoleWisePanel({ feature, allowedRoles }: { feature: DeleteFeature; allowedRoles: string[] }) {
  const qc = useQueryClient();

  const addGrant = useMutation({
    mutationFn: (roleCode: string) => deleteAccessService.addRoleGrant(feature, roleCode),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['delete-access-config', feature] }); toast.success('Role added'); },
    onError: () => toast.error('Failed to update'),
  });

  const removeGrant = useMutation({
    mutationFn: (roleCode: string) => deleteAccessService.removeRoleGrant(feature, roleCode),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['delete-access-config', feature] }); toast.success('Role removed'); },
    onError: () => toast.error('Failed to update'),
  });

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5 space-y-4">
      <div>
        <p className="text-sm font-semibold">Allowed Roles</p>
        <p className="text-xs text-muted-foreground mt-0.5">Only users with these roles can delete records.</p>
      </div>
      <div className="space-y-2">
        {ROLES.map((role) => {
          const checked = allowedRoles.includes(role.code);
          const pending = addGrant.isPending || removeGrant.isPending;
          return (
            <label key={role.code} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border/40 px-4 py-3 hover:bg-muted/30 transition-colors">
              <input
                type="checkbox"
                checked={checked}
                disabled={pending}
                onChange={() => { if (checked) removeGrant.mutate(role.code); else addGrant.mutate(role.code); }}
                className="h-4 w-4 rounded border-border/60 text-primary focus:ring-primary/30"
              />
              <span className="text-sm font-medium">{role.label}</span>
              {checked && (
                <span className="ml-auto inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  Allowed
                </span>
              )}
            </label>
          );
        })}
      </div>
      {allowedRoles.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          No roles selected — no one can delete records in this mode.
        </div>
      )}
    </div>
  );
}

// ── Person-wise Panel ──────────────────────────────────────────────────────────

const GRANTS_PAGE_SIZE = 20;

function PersonWisePanel({ feature, grants }: { feature: DeleteFeature; grants: DeleteAccessPersonGrant[] }) {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(grants.length / GRANTS_PAGE_SIZE));
  const pagedGrants = grants.slice((page - 1) * GRANTS_PAGE_SIZE, page * GRANTS_PAGE_SIZE);

  const addGrant = useMutation({
    mutationFn: (userId: string) => deleteAccessService.addPersonGrant(feature, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['delete-access-config', feature] });
      setShowModal(false);
      toast.success('Person added');
    },
    onError: () => toast.error('Failed to add'),
  });

  const removeGrant = useMutation({
    mutationFn: (grantId: string) => deleteAccessService.removePersonGrant(feature, grantId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['delete-access-config', feature] }); toast.success('Person removed'); },
    onError: () => toast.error('Failed to remove'),
  });

  const columns: Column<DeleteAccessPersonGrant>[] = [
    {
      key: 'employee',
      header: 'Can Delete',
      cell: (g) => <EmployeeCell first={g.first_name} last={g.last_name} code={g.employee_code} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (g) => (
        <Button size="icon" variant="ghost" onClick={() => removeGrant.mutate(g.id)} disabled={removeGrant.isPending}>
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Allowed Persons</p>
          <p className="text-xs text-muted-foreground mt-0.5">Only listed employees can delete records.</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4" /> Add Person
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={pagedGrants}
        rowKey={(g) => g.id}
        empty="No persons added yet — click Add Person to get started."
        pagination={grants.length > 0 ? { page, totalPages, total: grants.length, onPageChange: setPage } : undefined}
      />

      <AddPersonModal
        open={showModal}
        feature={feature}
        onClose={() => setShowModal(false)}
        isPending={addGrant.isPending}
        onAdd={(uid) => addGrant.mutate(uid)}
      />
    </div>
  );
}

// ── Feature Panel (mode selector + conditional panel) ─────────────────────────

function FeaturePanel({ feature }: { feature: DeleteFeature }) {
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['delete-access-config', feature],
    queryFn: () => deleteAccessService.getConfig(feature),
  });

  const updateMode = useMutation({
    mutationFn: (mode: string) => deleteAccessService.updateMode(feature, mode as Mode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['delete-access-config', feature] });
      void qc.invalidateQueries({ queryKey: ['delete-access', 'my-permission', feature] });
      toast.success('Permission mode updated');
    },
    onError: () => toast.error('Failed to update mode'),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-muted/60" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm text-destructive">
        Failed to load permission config.
      </div>
    );
  }

  const { mode, allowed_roles, person_grants } = data;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/60 bg-card p-5 space-y-3">
        <p className="text-sm font-semibold">Permission Mode</p>
        <div className="space-y-2">
          {MODE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3.5 transition-colors ${
                mode === opt.value
                  ? 'border-primary/40 bg-primary/5'
                  : 'border-border/60 bg-card hover:bg-muted/30'
              }`}
            >
              <input
                type="radio"
                name={`${feature}-delete-mode`}
                value={opt.value}
                checked={mode === opt.value}
                disabled={updateMode.isPending}
                onChange={() => updateMode.mutate(opt.value)}
                className="mt-0.5 h-4 w-4 text-primary focus:ring-primary/30"
              />
              <div>
                <p className={`text-sm font-semibold ${mode === opt.value ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {mode === 'role_wise'   && <RoleWisePanel   feature={feature} allowedRoles={allowed_roles} />}
      {mode === 'person_wise' && <PersonWisePanel feature={feature} grants={person_grants} />}
    </div>
  );
}

// ── Main Section ───────────────────────────────────────────────────────────────

const FEATURE_TABS: { key: DeleteFeature; label: string }[] = [
  { key: 'leave',   label: 'Leave Delete' },
  { key: 'penalty', label: 'Penalty Delete' },
];

export function DeleteAccessSection() {
  const [activeFeature, setActiveFeature] = useState<DeleteFeature>('leave');

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50 border border-border/60">
          <ShieldCheck className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h2 className="text-base font-semibold">Delete Access Control</h2>
          <p className="text-xs text-muted-foreground">Configure who can delete leave and penalty records.</p>
        </div>
      </div>

      <div className="flex gap-1 rounded-lg border border-border/60 bg-muted/30 p-1 w-fit">
        {FEATURE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveFeature(tab.key)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              activeFeature === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <FeaturePanel key={activeFeature} feature={activeFeature} />
    </div>
  );
}
